import { useEffect, useState } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import CryptoJS from "crypto-js"
import moment from "moment"
import {
    Calendar,
    ChevronRight,
    Circle,
    HelpCircle,
    LifeBuoy,
    Lock,
    Unlock,
    Play,
    PlayCircle,
    User,
    Users,
    Target,
    LogOut
} from "react-feather"
import { Footer } from "../../../Footer/Footer"
import { Header } from "../../Header/Header"
import { VideoPlayer } from "./VideoPlayer"
import { WebinarDetailsModal } from "./WebinarDetailsModal"
import { UserLogin } from "./UserLogin"
import { logout } from "../../../../helper/Context/action"
import { useAuthDispatch, useAuthState } from "../../../../helper/Context/context"
import { LogoLoader } from "../../../../helper/Loaders/LogoLoader"
import { BaseUrl } from "../../../../helper/BaseUrl"
import "./AllWebinarVideos.css"
import { GET_M_WebinarVideoMaster_SelectAPI } from "../../../../helper/Redux/Slices/Admin/WebinarVideoMasterSlice/WebinarVideoMasterSlice"

const decryptVideosDetails = (videosdetails) => {
    if (!videosdetails) return []
    if (Array.isArray(videosdetails)) return videosdetails
    if (typeof videosdetails === "object" && videosdetails !== null) return [videosdetails]

    try {
        const key = CryptoJS.enc.Utf8.parse("b14ca5898a4e4133bbce2ea2315a1916")
        const iv = CryptoJS.enc.Utf8.parse("fedcba9876543210")
        const decoded = decodeURIComponent(videosdetails).replace(/ /g, "+")
        const bytes = CryptoJS.AES.decrypt(decoded, key, {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        })
        const decryptedText = bytes.toString(CryptoJS.enc.Utf8)
        if (!decryptedText) return []
        const decryptedData = JSON.parse(decryptedText)
        return Array.isArray(decryptedData) ? decryptedData : [decryptedData]
    } catch (error) {
        try {
            const decoded = decodeURIComponent(videosdetails).replace(/ /g, "+")
            const bytes = CryptoJS.AES.decrypt(decoded, "b14ca5898a4e4133bbce2ea2315a1916")
            const decryptedText = bytes.toString(CryptoJS.enc.Utf8)
            if (!decryptedText) return []
            const decryptedData = JSON.parse(decryptedText)
            return Array.isArray(decryptedData) ? decryptedData : [decryptedData]
        } catch (err) {
            console.log("Invalid Data", err)
            return []
        }
    }
}

const normalizeUrl = (url) => {
    if (!url || typeof url !== "string") return ""
    let trimmed = url.trim()
    if (!trimmed) return ""

    // If relative path without protocol, prefix with BaseUrl
    if (!/^https?:\/\//i.test(trimmed) && !trimmed.startsWith("data:") && !trimmed.startsWith("blob:")) {
        const cleanedPath = trimmed.replace(/^\/+/, "")
        trimmed = `${BaseUrl}/${cleanedPath}`
    }

    // Clean duplicate slashes except protocol: e.g. https://apimilso.cdat.work//Upload -> https://apimilso.cdat.work/Upload
    return trimmed.replace(/([^:]\/)\/+/g, "$1")
}

const getVideoUrl = (videoOrItem, fallbackItem) => {
    if (!videoOrItem && !fallbackItem) return ""
    if (typeof videoOrItem === "string" && (videoOrItem.startsWith("http") || videoOrItem.startsWith("/"))) {
        return normalizeUrl(videoOrItem)
    }

    const pool = []
    const addCandidate = (val) => {
        if (!val) return
        if (typeof val === "string" && (val.startsWith("http") || val.startsWith("/"))) {
            pool.push({ url: val })
        } else if (Array.isArray(val)) {
            val.forEach(addCandidate)
        } else if (typeof val === "object") {
            pool.push(val)
            if (typeof val.videosdetails === "string" && val.videosdetails.trim()) {
                const decrypted = decryptVideosDetails(val.videosdetails)
                if (Array.isArray(decrypted)) decrypted.forEach(addCandidate)
            }
        }
    }

    addCandidate(videoOrItem)
    addCandidate(fallbackItem)

    for (const obj of pool) {
        if (!obj || typeof obj !== "object") continue
        const direct =
            obj?.videoPath ||
            obj?.videoUrl ||
            obj?.VideoPath ||
            obj?.VideoURL ||
            obj?.VideoUrl ||
            obj?.url ||
            obj?.Url

        if (direct && typeof direct === "string" && direct.trim()) {
            return normalizeUrl(direct)
        }

        for (const [key, value] of Object.entries(obj)) {
            if (
                typeof value === "string" &&
                value.trim() &&
                /video|player|embed|stream/i.test(key)
            ) {
                return normalizeUrl(value)
            }
        }
    }

    return ""
}

const getThumbnailUrl = (videoOrItem, fallbackItem) => {
    if (!videoOrItem && !fallbackItem) return ""
    if (typeof videoOrItem === "string" && (videoOrItem.startsWith("http") || videoOrItem.startsWith("/"))) {
        return normalizeUrl(videoOrItem)
    }

    const pool = []
    const addCandidate = (val) => {
        if (!val) return
        if (typeof val === "string" && (val.startsWith("http") || val.startsWith("/"))) {
            pool.push({ url: val })
        } else if (Array.isArray(val)) {
            val.forEach(addCandidate)
        } else if (typeof val === "object") {
            pool.push(val)
            if (typeof val.videosdetails === "string" && val.videosdetails.trim()) {
                const decrypted = decryptVideosDetails(val.videosdetails)
                if (Array.isArray(decrypted)) decrypted.forEach(addCandidate)
            }
        }
    }

    addCandidate(videoOrItem)
    addCandidate(fallbackItem)

    for (const obj of pool) {
        if (!obj || typeof obj !== "object") continue
        const direct =
            obj?.thumbnailPath ||
            obj?.thumbnailUrl ||
            obj?.ThumbnailPath ||
            obj?.ThumbnailURL ||
            obj?.ThumbnailUrl ||
            obj?.imagePath ||
            obj?.ImagePath ||
            obj?.photoPath ||
            obj?.PhotoPath

        if (direct && typeof direct === "string" && direct.trim()) {
            return normalizeUrl(direct)
        }

        for (const [key, value] of Object.entries(obj)) {
            if (
                typeof value === "string" &&
                value.trim() &&
                /thumb|poster|image|photo/i.test(key)
            ) {
                return normalizeUrl(value)
            }
        }
    }

    return ""
}

const formatDuration = (val) => {
    if (!val && val !== 0) return ""
    const str = String(val).trim()
    if (!str || str === "0" || str === "00:00") return ""

    if (str.includes(":") || /[a-zA-Z]/.test(str)) {
        return str
    }

    const num = Number(str)
    if (!isNaN(num) && num > 0) {
        if (num > 3600) {
            const h = Math.floor(num / 3600)
            const m = Math.floor((num % 3600) / 60)
            const s = Math.floor(num % 60)
            return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
        } else if (num > 60) {
            const m = Math.floor(num / 60)
            const s = Math.floor(num % 60)
            return `${m}:${String(s).padStart(2, "0")}`
        } else {
            return `${num} min`
        }
    }
    return str
}

const getVideoDuration = (videoOrItem, fallbackItem) => {
    if (!videoOrItem && !fallbackItem) return ""

    const pool = []
    const addCandidate = (val) => {
        if (!val) return
        if (Array.isArray(val)) {
            val.forEach(addCandidate)
        } else if (typeof val === "object") {
            pool.push(val)
            if (typeof val.videosdetails === "string" && val.videosdetails.trim()) {
                const decrypted = decryptVideosDetails(val.videosdetails)
                if (Array.isArray(decrypted)) decrypted.forEach(addCandidate)
            }
        }
    }

    addCandidate(videoOrItem)
    addCandidate(fallbackItem)

    for (const obj of pool) {
        if (!obj || typeof obj !== "object") continue
        const direct =
            obj?.videoLength ||
            obj?.duration ||
            obj?.VideoLength ||
            obj?.Duration ||
            obj?.length ||
            obj?.Length

        if (direct !== undefined && direct !== null && String(direct).trim()) {
            const formatted = formatDuration(direct)
            if (formatted) return formatted
        }

        for (const [key, value] of Object.entries(obj)) {
            if (
                value !== undefined &&
                value !== null &&
                String(value).trim() &&
                /duration|length|time/i.test(key)
            ) {
                const formatted = formatDuration(value)
                if (formatted) return formatted
            }
        }
    }

    return ""
}

const WebinarCardDuration = ({ explicitDuration, videoUrl }) => {
    const [duration, setDuration] = useState(
        explicitDuration && explicitDuration !== "00:00" && explicitDuration !== "0"
            ? explicitDuration
            : ""
    )

    useEffect(() => {
        if (explicitDuration && explicitDuration !== "00:00" && explicitDuration !== "0") {
            setDuration(explicitDuration)
            return
        }

        if (!videoUrl) return

        let isMounted = true
        const videoElement = document.createElement("video")
        videoElement.preload = "metadata"
        videoElement.src = videoUrl

        const onLoaded = () => {
            if (!isMounted) return
            const d = videoElement.duration
            if (Number.isFinite(d) && d > 0) {
                const totalSecs = Math.round(d)
                const hrs = Math.floor(totalSecs / 3600)
                const mins = Math.floor((totalSecs % 3600) / 60)
                const secs = totalSecs % 60
                if (hrs > 0) {
                    setDuration(`${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`)
                } else {
                    setDuration(`${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`)
                }
            }
        }

        videoElement.addEventListener("loadedmetadata", onLoaded)

        return () => {
            isMounted = false
            videoElement.removeEventListener("loadedmetadata", onLoaded)
            videoElement.src = ""
        }
    }, [explicitDuration, videoUrl])

    if (!duration || duration === "00:00" || duration === "0") {
        return null
    }

    return (
        <span className="webinar-duration">
            <Play size={14} />
            {duration}
        </span>
    )
}

const isItemPaid = (item) => {
    if (item?.paid !== undefined) return Boolean(item.paid)
    if (item?.isPaid !== undefined) return Boolean(item.isPaid)
    const videos = decryptVideosDetails(item?.videosdetails)
    const videoUrl = getVideoUrl(videos[0])
    return Boolean(videoUrl)
}

export const AllWebinarVideos = () => {
    const navigate = useNavigate()

    const [searchparams, setSearchParams] = useSearchParams()
    const userDetails = useAuthState()
    const { year, Month, Language } = userDetails
    const { UserID, token } = userDetails?.user || {}
    const dispatch = useDispatch()

    const [perPageCount] = useState(50)
    const [From, setFrom] = useState(1)
    const [To, setTo] = useState(50)

    const [isAuthenticated, setIsAuthenticated] = useState(
        () => Boolean(sessionStorage.getItem("A454A"))
    )
    const [showLogin, setShowLogin] = useState(false)
    const requestedFilter = searchparams.get("filter")
    const activeFilter = ["all", "paid", "unpaid"].includes(requestedFilter) ? requestedFilter : "all"
    const [selectedWebinar, setSelectedWebinar] = useState(null)



    const handleTileActivation = (event) => {
        if (event.type === "keydown" && event.key !== "Enter" && event.key !== " ") {
            return
        }

        if (event.type === "keydown") {
            event.preventDefault()
        }
         if (isAuthenticated) {
            navigate('/WebinarVideos')
        } else {
            setShowLogin(true)
        }
    }


    useEffect(() => {
        handleGetData()
    }, [year?.Label, Month?.ID, Language?.Label])

    const handleGetData = (searchTerm = "") => {
        const data = {
            WebinarVideoID: 0,
            SearchTerm: searchTerm,
            Flag: "Details",
            M_UserID: UserID ? UserID : 0,
            Language: Language?.Label,
            FromTop: From,
            ToTop: To,
            token: token,
        }

        dispatch(GET_M_WebinarVideoMaster_SelectAPI({ data }))
    }

    const { isLoading, TableData } = useSelector((state) => state.GET_M_WebinarVideoMaster_SelectData)

    const filteredTableData = TableData && TableData?.length > 0
        ? TableData.filter((item) => {
            if (activeFilter === "all") return true
            const isPaid = isItemPaid(item)
            return activeFilter === "paid" ? isPaid : !isPaid
        })
        : []

    return (
        <>
            {isLoading && <LogoLoader />}
            <div className="container-fluid webinar-page" onLoad={() => window.scrollTo(0, 0)}>
                <Header actives="Webinars" subactive="Events" />

                <main className="webinar-main">
                    {!showLogin ? (
                        <section
                            className="webinar-hero mb-4"
                            aria-labelledby="webinar-page-title"
                            style={{
                                backgroundImage: `url("${process.env.PUBLIC_URL}/Static/Assets/Img/Webinars/page-heading-bg.png")`,
                            }}
                        >
                            <div className="webinar-hero-copy">
                                <p className="webinar-eyebrow"><span /> MILSO ACADEMY</p>
                                <h1 id="webinar-page-title">Webinar Videos</h1>
                                <p>Explore MILSO Academy recordings and webinar<br className="d-none d-md-block" /> on maritime insurance.</p>
                            </div>
                        </section>
                    ) : (
                        <div></div>
                    )}

                    <div className="webinar-layout row">
                       
                        <section className={`webinar-results ${isAuthenticated ? "col-12 col-xl-10 col-lg-9" : "col-12 col-lg-12"} `} aria-label="Webinar video topics">

                            {showLogin && !isAuthenticated ? (
                                <UserLogin
                                    onLoginSuccess={() => {
                                        setIsAuthenticated(true)
                                        setShowLogin(false)
                                        // handleGetData()
                                        navigate('/WebinarVideos')
                                    }}
                                     name="Webinar"
                                />
                            ) : (
                                <div className="webinar-card-grid">
                                    {filteredTableData && filteredTableData?.length > 0 ? filteredTableData?.map((item) => {
                                        const videos = decryptVideosDetails(item?.videosdetails)
                                        const firstVideo = videos[0] || {}
                                        const videoUrl = getVideoUrl(firstVideo)
                                        const fallbackThumbnail = `${process.env.PUBLIC_URL || ""}/Static/Assets/Img/Seminar_Img.jpeg`
                                        const resolvedThumbnail = getThumbnailUrl(firstVideo)
                                        const thumbnailUrl = resolvedThumbnail || fallbackThumbnail
                                        const isPaid = isItemPaid(item)
                                        const duration = getVideoDuration(firstVideo)
                                        const publishDate = item.createdDate ? moment(item.createdDate).format("DD MMM YYYY") : "-"
                                        const views = item.totalCount ? item.totalCount : "0"
                                        const title = item.titleName ? item.titleName : "-"

                                      
                                        return (
                                            <article
                                                className="webinar-card"
                                                key={item.webinarVideoID || item.seminarVideoID || item.rowNum}
                                                role={"button"}
                                                tabIndex={0}
                                                aria-label={`Sign in to access ${title}`}
                                                onClick={handleTileActivation}
                                                onKeyDown={handleTileActivation}
                                            >
                                                <div
                                                    className="webinar-thumbnail"
                                                    onContextMenu={(e) => e.preventDefault()}
                                                    style={{ position: "relative", overflow: "hidden", userSelect: "none", WebkitUserSelect: "none" }}
                                                >
                                                    <img
                                                        src={thumbnailUrl}
                                                        alt={`Thumbnail for ${title}`}
                                                        loading="lazy"
                                                        draggable="false"
                                                        onContextMenu={(e) => e.preventDefault()}
                                                        onDragStart={(e) => e.preventDefault()}
                                                        style={{
                                                            userSelect: "none",
                                                            WebkitUserSelect: "none",
                                                            WebkitUserDrag: "none",
                                                            pointerEvents: "none",
                                                        }}
                                                        onError={(e) => {
                                                            console.warn(`[Thumbnail Load Failed] "${title}":`, thumbnailUrl, "Falling back to:", fallbackThumbnail)
                                                            e.target.onerror = null
                                                            e.target.src = fallbackThumbnail
                                                        }}
                                                    />
                                                   
                                                    <WebinarCardDuration explicitDuration={duration} videoUrl={videoUrl} />
                                                </div>
                                                <div className="webinar-card-body">
                                                    <div className="webinar-publisher">
                                                        <img src="/Static/Assets/Img/logo.png" alt="" />
                                                        <span>MILSO Academy</span>
                                                    </div>
                                                    <h2>{title}</h2>
                                                    <div className="webinar-card-footer">
                                                        <span className="webinar-format d-flex gap-2 align-items-center">
                                                            <span><Calendar size={13} /> {publishDate}</span> | <span><Users size={13} /> {views} Views</span>
                                                        </span>

                                                    </div>
                                                </div>
                                            </article>
                                        )
                                    }) : (
                                        <div className="col-12 text-center py-5 text-muted">
                                            No Data Found ...
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>
                    </div>
                </main>

                <WebinarDetailsModal
                    video={selectedWebinar}
                    onClose={() => setSelectedWebinar(null)}
                />
              
                <Footer />
            </div>
        </>
    )
}

